// Currency utilities: central place to format and display money values
// Default to INR to preserve existing behaviour for users who haven't set a preference.

const CURRENCY_MAP = {
  INR: { symbol: '₹', locale: 'en-IN' },
  USD: { symbol: '$', locale: 'en-US' },
  EUR: { symbol: '€', locale: 'en-GB' },
  GBP: { symbol: '£', locale: 'en-GB' },
  AUD: { symbol: 'A$', locale: 'en-AU' },
};

export const SUPPORTED_CURRENCIES = Object.keys(CURRENCY_MAP);

export const getCurrencySymbol = (code = 'INR') =>
  CURRENCY_MAP[code]?.symbol || code;

export const getCurrencyLocale = (code = 'INR') =>
  CURRENCY_MAP[code]?.locale || 'en-US';

export const formatCurrency = (amount = 0, code = 'INR', opts = {}) => {
  const locale = getCurrencyLocale(code);
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: code,
    minimumFractionDigits: opts.minimumFractionDigits ?? 0,
    maximumFractionDigits: opts.maximumFractionDigits ?? 0,
  }).format(Number.isFinite(amount) ? amount : 0);
};

// --- Conversion helpers ---
// Rates are stored as: 1 USD = rates[CODE]
// Approximate fallback (October 2026), used only if live rates can't be fetched.
export const DEFAULT_RATES = {
  USD: 1,
  INR: 94.8,
  EUR: 0.89,
  GBP: 0.75,
  AUD: 1.43,
};

let cachedRates = DEFAULT_RATES;
let cachedAt = 0;

export const fetchUsdRates = async () => {
  const now = Date.now();
  if (cachedRates && now - cachedAt < 60 * 60 * 1000) return cachedRates;
  try {
    // Free European Central Bank reference rates; no API key or cost.
    const res = await fetch('https://api.frankfurter.dev/v1/latest?base=USD');
    if (!res.ok) throw new Error(`Rates request failed (${res.status})`);
    const data = await res.json();
    if (data?.rates) {
      cachedRates = { USD: 1, ...data.rates };
      cachedAt = now;
      return cachedRates;
    }
  } catch (err) {
    console.warn('Exchange rate fetch failed', err);
  }
  return cachedRates;
};

export const convertCurrency = (amount = 0, from = 'USD', to = 'USD', rates = DEFAULT_RATES) => {
  if (!Number.isFinite(amount) || from === to) return Number(amount) || 0;
  const map = rates || DEFAULT_RATES;
  const usdPerFrom = 1 / (map[from] || map.USD || 1);
  const amountInUsd = amount * usdPerFrom;
  const rateTo = map[to] || map.USD || 1;
  return amountInUsd * rateTo;
};

export default {
  SUPPORTED_CURRENCIES,
  getCurrencySymbol,
  getCurrencyLocale,
  formatCurrency,
  DEFAULT_RATES,
  fetchUsdRates,
  convertCurrency,
};
